import moment from 'moment'
import { FC } from 'react'
import {
  SpectrumBankAccountStatus,
  SpectrumBankAccountStatusLabel,
  SpectrumCurrencyTypeEnum,
  SpectrumCurrencyTypeLabel,
} from '../../../../common/enums/spectrumBankAccountEnum'
import {
  ISpectrumBankAccount,
  ISpectrumBankAccountGlobalResult,
} from '../../../../common/interfaces'
import { useSpectrumBankAccountImageViewer } from '../hooks/useSpectrumBankAccountImageViewer'
import {
  ISpectrumBankAccountFilters,
  ISpectrumBankAccountSelection,
} from '../hooks/useSpectrumBankAccounts'
import { SPECTRUM_BANK_ACCOUNT_STATUS_BADGE } from '../utils/spectrumBankAccountStatusBadge'

type ISpectrumBankAccountRow = ISpectrumBankAccount &
  Partial<
    Pick<
      ISpectrumBankAccountGlobalResult,
      'descripCliente' | 'tipoIdentidad' | 'tipoIdentidadDesc' | 'numIdentidad'
    >
  >

interface IProps {
  rows: ISpectrumBankAccountRow[]
  filters: ISpectrumBankAccountFilters
  selected: ISpectrumBankAccountSelection | null
  onToggleSelected: (row: ISpectrumBankAccountSelection) => void
}

const cellTextStyle = { fontSize: '16px', fontWeight: 500, lineHeight: '24px', color: '#69778f' }

const headerTextStyle = {
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: '22px',
  letterSpacing: '0.16px',
  color: '#95A0B2',
}

const badgeTextStyle = { fontSize: '14px', fontWeight: 700, lineHeight: '20px' }

const maskCci = (cci: string) => (cci?.length > 4 ? `*** ${cci.slice(-4)}` : cci)

const rowIdentity = (row: ISpectrumBankAccountRow, filters: ISpectrumBankAccountFilters) => ({
  nombre: row.descripCliente ?? '—',
  numIdentidad: row.numIdentidad ?? filters.numIdentidad,
  tipoIdentidad: row.tipoIdentidad ?? filters.tipoIdentidad,
  tipoIdentidadDesc: row.tipoIdentidadDesc ?? '—',
})

export const SpectrumBankAccountTable: FC<IProps> = ({
  rows,
  filters,
  selected,
  onToggleSelected,
}) => {
  const { viewImage, downloadImage, isDownloading } = useSpectrumBankAccountImageViewer()

  return (
    <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
      <thead>
        <tr className='text-uppercase' style={headerTextStyle}>
          <th></th>
          <th className='min-w-200px'>Nombre</th>
          <th className='min-w-140px'>Tipo de Documento</th>
          <th>N° Documento</th>
          <th className='min-w-140px'>Fecha</th>
          <th>Moneda</th>
          <th className='min-w-150px'>Tipo de cuenta</th>
          <th className='min-w-250px'>Banco</th>
          <th>N° de cuenta</th>
          <th>CCI</th>
          <th>Estado</th>
          <th className='min-w-100px text-center'>Adjuntos</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const identity = rowIdentity(row, filters)
          const rowKey = `${row.codCliente}-${row.numSecBancario}`
          const isSelected =
            selected?.codCliente === row.codCliente &&
            selected?.numSecBancario === row.numSecBancario
          const statusBadge = SPECTRUM_BANK_ACCOUNT_STATUS_BADGE[row.estado]
          const badge = statusBadge && {
            label: SpectrumBankAccountStatusLabel[row.estado],
            ...statusBadge,
          }

          return (
            <tr key={rowKey}>
              <td>
                {row.estado === SpectrumBankAccountStatus.PENDING && (
                  <div className='form-check form-check-custom form-check-solid'>
                    <input
                      type='checkbox'
                      className='form-check-input'
                      style={{
                        cursor: 'pointer',
                        borderRadius: '4px',
                        borderColor: '#bbb9b9',
                        border: 'solid 1px #bbb9b9',
                      }}
                      checked={isSelected}
                      onChange={() =>
                        onToggleSelected({
                          codCliente: row.codCliente,
                          tipoIdentidad: identity.tipoIdentidad,
                          numIdentidad: identity.numIdentidad,
                          numSecBancario: row.numSecBancario,
                          estado: row.estado,
                        })
                      }
                    />
                  </div>
                )}
              </td>
              <td>
                <span className='d-block' style={cellTextStyle}>
                  {identity.nombre}
                </span>
              </td>
              <td>
                <span className='d-block' style={cellTextStyle}>
                  {identity.tipoIdentidadDesc}
                </span>
              </td>
              <td>
                <span className='d-block' style={cellTextStyle}>
                  {identity.numIdentidad}
                </span>
              </td>
              <td>
                <span className='d-block' style={cellTextStyle}>
                  {row.fechaRegistro ? moment(row.fechaRegistro).format('DD/MM/YYYY') : ''}
                </span>
              </td>
              <td>
                <span className='d-block' style={cellTextStyle}>
                  {SpectrumCurrencyTypeLabel[row.tipoMoneda as SpectrumCurrencyTypeEnum] ??
                    row.tipoMonedaDesc}
                </span>
              </td>
              <td>
                <span className='d-block' style={cellTextStyle}>
                  {row.tipoCtaCteDesc}
                </span>
              </td>
              <td>
                <span className='d-block' style={cellTextStyle}>
                  {row.bancoDesc}
                </span>
              </td>
              <td>
                <span className='d-block' style={cellTextStyle}>
                  {row.nroCtaCte}
                </span>
              </td>
              <td>
                <span className='d-block' style={cellTextStyle}>
                  {row.cci}
                </span>
              </td>
              <td>
                {badge ? (
                  <span
                    className='badge rounded'
                    style={{ ...badgeTextStyle, backgroundColor: badge.bg, color: badge.color }}
                  >
                    {badge.label}
                  </span>
                ) : null}
              </td>
              <td className='text-center'>
                {row.image_url ? (
                  <div className='d-flex align-items-center justify-content-center'>
                    {isDownloading(row.image_url) ? (
                      <div className='text-center text-muted'>
                        <div
                          className='spinner-border'
                          style={{ width: '18px', height: '18px' }}
                          role='status'
                        ></div>
                      </div>
                    ) : (
                      <div
                        onClick={() => downloadImage(row.image_url!)}
                        className='iconButton p-1 cursor-pointer'
                        title='Descargar imagen'
                      >
                        <i className='bi bi-download fs-3 m-2'></i>
                      </div>
                    )}
                    <div
                      onClick={() => viewImage(row.image_url!)}
                      className='iconButton p-1 cursor-pointer'
                      title='Ver imagen'
                    >
                      <i className='bi bi-card-image fs-3 m-2'></i>
                    </div>
                  </div>
                ) : (
                  <span className='text-muted'>--</span>
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
